import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Subject, timer, of } from 'rxjs';
import { map, distinctUntilChanged, takeUntil, filter, take, startWith, debounceTime, tap, switchMap } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { AuthStore } from '@core/services/auth/auth.store';
import { UnifiedLoadingService } from '@core/services/infra/unified-loading.service';
import { AppCoordinationService } from '@core/services/infra/app-coordination.service';
import { PropertyListStateService } from '@core/services/data/property-list-state.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { coreStoresApplicable } from '@shared/definitions/workspace';
import { toObservable } from '@angular/core/rxjs-interop';
import type { WorkspaceSwitchRailEntry } from '@shared/utils/workspace-identity.util';

/**
 * Curtain chrome. Escalates by elapsed wait — `minimal` for the first 2 s,
 * then `tour` if the curtain is still up. `workspace-switch` is pinned by the
 * switch marker and never escalates. The legacy `signup` brand-splash mode is
 * gone: signup shares the same adaptive surface.
 */
export type BootLoadingUiMode = 'minimal' | 'tour' | 'workspace-switch';

/**
 * Payload transported in sessionStorage between the click on "switch
 * workspace" (ToolbarWorkspaceSwitcherComponent.activate()) and the hard
 * reload that follows. `rail` is empty for a legacy {fromName,toName} marker
 * or when the palette didn't have enough workspaces to build one — both cases
 * degrade to the same "destination only" composition in the curtain.
 */
export interface WorkspaceSwitchMarker {
  toId: string;
  toName: string;
  fromName: string;
  rail: WorkspaceSwitchRailEntry[];
}

export interface AuthenticatedLoadingState {
  isLoading: boolean;
  phase: 'initializing' | 'loading-core' | 'loading-data' | 'finalizing' | 'complete';
  progress: number;
  message: string;
  subMessage?: string;
  completedServices: string[];
  totalServices: number;
  showDetailedProgress: boolean;
  estimatedTimeRemaining?: number;
  /**
   * Optional override for the 4 phase indicator labels
   * (default: Initialize / Core Data / Enrich Data / Finalize).
   * Used during fresh-signup initialization so the user sees
   * Account / Workspace / Permissions / Ready.
   */
  phaseLabels?: [string, string, string, string];
  /**
   * Optional override for the loading tip shown when an estimate is
   * available. Used by signup-init mode to surface workspace-specific
   * context instead of the generic dashboard tips.
   */
  loadingTip?: string;
  /**
   * Which full-screen chrome to render. Starts `minimal`; escalates to
   * `tour` after {@link AuthenticatedLoadingCoordinatorService.TOUR_ESCALATE_MS}
   * if the curtain is still up.
   */
  uiMode?: BootLoadingUiMode;
  /** Present only when uiMode === 'workspace-switch'. See {@link WorkspaceSwitchMarker}. */
  workspaceSwitch?: WorkspaceSwitchMarker;
  /** Params to interpolate into `message` when it's an i18n key with a placeholder. */
  messageParams?: Record<string, string>;
  /**
   * Anti-flash: the overlay DOM stays hidden until this flips true (~250 ms
   * after auth load starts, or immediately for signup-init). Lets a hot cache
   * finish under the HTML splash without mounting a second curtain.
   */
  revealOverlay: boolean;
  /**
   * Where the brand mark's build should pick its clock up.
   *
   * `continue` at boot: `index.html`'s splash started the same build at page
   * load, and this curtain relieves it mid-way — measured at ~670 ms, with the
   * roof still drawing. The curtain offsets the timeline by the elapsed page
   * time so the build carries on instead of rewinding or jumping to the end.
   * `restart` when the curtain opens over an app already on screen — signing
   * in, signing up, switching workspace — where nothing has been building.
   */
  markPhase?: BootMarkPhase;
}

/** @see AuthenticatedLoadingState.markPhase */
export type BootMarkPhase = 'restart' | 'continue';

/** Step values written by the backend post-signup pipeline. */
export type SignupInitBackendStep =
  | 'user_created'
  | 'workspace_created'
  | 'claims_ready';

export interface SignupInitProgress {
  step?: SignupInitBackendStep | string;
  progress?: number;
  message?: string;
}

/**
 * Checkpoints the CLIENT can observe on its own, without waiting on the
 * backend pipeline.
 *
 * The email-signup path writes `users/{uid}` with the client SDK and never
 * calls the `createUserDocument` callable — the only emitter of the backend's
 * `user_created` / 33 % step. So the first backend status the overlay ever
 * receives is `workspace_created` / 66 %, written by the `onUserCreatedWorkspace`
 * trigger. The gap spans a Gen2 cold start, a batch commit and a second
 * trigger, during which the bar sat frozen at the 5 % set on entry.
 *
 * These milestones fill that gap with progress the client already knows to be
 * true. Their ceilings sit strictly below the next backend step, so a real
 * backend status always raises the bar rather than being swallowed by the
 * monotonic clamp.
 */
export type SignupClientMilestone =
  /** Firebase Auth account exists. */
  | 'account_created'
  /** `users/{uid}` written — the CF trigger chain is now in flight. */
  | 'user_doc_written'
  /** Listening on `signup_status/initialization`. */
  | 'awaiting_backend'
  /** Claims refreshed and workspaces loaded; only navigation is left. */
  | 'session_ready';

export interface LoadingPhase {
  name: string;
  weight: number;
  services: string[];
  message: string;
  subMessage?: string;
}

/**
 * @domain Core/Services
 * 
 * Authenticated Loading Coordinator - Manages smooth loading experience for logged-in users
 * 
 * This service:
 * - Coordinates all loading states for authenticated users
 * - Prevents UI flickering by showing loading screen until all critical data is ready
 * - Provides detailed progress tracking with phases and estimates
 * - Manages the transition from loading to fully loaded application
 * - Ensures smooth UX for returning users with tokens
 */
@Injectable({
  providedIn: 'root'
})
export class AuthenticatedLoadingCoordinatorService implements OnDestroy {
  private destroy$ = new Subject<void>();

  /**
   * The logout flow ends with `window.location.assign('/auth/login')` — a
   * hard reload. When the new page boots, this service is constructed
   * before Firebase Auth has resolved, so without this guard we'd flash
   * the "Starting application…" splash for ~100-300 ms on top of the
   * login form, which the user has nothing to load anyway.
   *
   * Detected at construction time from the URL path, so it's stable for
   * this whole boot cycle.
   */
  private readonly bootedOnAuthRoute = AuthenticatedLoadingCoordinatorService.isAuthRoute(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  /**
   * @deprecated Kept for callers that still clear the old first-tab flag.
   * Tour vs minimal is now driven by elapsed wait ({@link TOUR_ESCALATE_MS}).
   */
  static readonly TOUR_SEEN_KEY = 'proporio_boot_tour_seen';

  /** Delay before mounting Angular overlay chrome (anti-flash on hot cache). */
  private static readonly OVERLAY_REVEAL_MS = 250;

  /** Escalate from minimal chrome to the guided tour after this wait. */
  static readonly TOUR_ESCALATE_MS = 2_000;

  /** Hard ceiling on how long the curtain may stay up, whatever it is waiting for. */
  private static readonly ABSOLUTE_FAILSAFE_MS = 12_000;

  // Loading state management
  private loadingStateSubject = new BehaviorSubject<AuthenticatedLoadingState>({
    isLoading: !this.bootedOnAuthRoute,
    phase: this.bootedOnAuthRoute ? 'complete' : 'initializing',
    progress: this.bootedOnAuthRoute ? 100 : 0,
    message: this.bootedOnAuthRoute ? 'shared.loadingScreen.ready' : 'shared.loadingScreen.starting',
    subMessage: this.bootedOnAuthRoute ? undefined : 'shared.loadingScreen.startingSub',
    completedServices: [],
    totalServices: 6,
    showDetailedProgress: !this.bootedOnAuthRoute,
    uiMode: this.bootedOnAuthRoute ? undefined : 'minimal',
    revealOverlay: false
  });

  /** True once progress + completion pipelines are subscribed for this boot. */
  private coordinating = false;

  /**
   * Set by {@link beginUserInitiatedTransition} and cleared when the curtain
   * comes down. It means the wait was started by a deliberate click, so the
   * 250 ms anti-flash delay is wrong twice over: there is no HTML splash to let
   * finish, and the screen the user just acted on is still standing behind.
   */
  private userInitiated = false;

  /**
   * Whether a build is already running on screen, so the next state to raise
   * the curtain must CONTINUE it rather than start its own.
   *
   * True from construction because a cold load always paints `index.html`'s
   * splash first, and that splash starts the build at page load. It goes false
   * again when the curtain comes down, so a sign-in later in the same page life
   * gets a build of its own — by then the splash is long gone.
   */
  private markAssembled = true;

  /** Cancels an in-flight reveal timer when loading completes early. */
  private revealTimer: ReturnType<typeof setTimeout> | null = null;

  /** Escalates minimal → tour after {@link TOUR_ESCALATE_MS}. */
  private tourEscalateTimer: ReturnType<typeof setTimeout> | null = null;

  /** Public/unauthenticated routes that should NEVER show the overlay at boot. */
  static isAuthRoute(pathname: string): boolean {
    const path = pathname.split('?')[0] || pathname;
    return (
      path.startsWith('/auth') ||
      path.startsWith('/login') ||
      path.startsWith('/register') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/verify-email') ||
      path.startsWith('/tenant/invitation') ||
      path.startsWith('/provider/claim') ||
      path === '/book' ||
      path.startsWith('/book/') ||
      path.startsWith('/estimation') ||
      path.startsWith('/charges/view') ||
      // Same family: a tokenised public landing, no auth guard (#514). An
      // authenticated user who opens one of these links still hit the full
      // "authenticated dashboard" loading path instead of the fast public
      // one — the boot watchdog only caught it on /whatsapp/confirm because
      // that route's fallback ($timeout) path took long enough to trip it.
      path.startsWith('/whatsapp/confirm') ||
      path.startsWith('/assemblies/view') ||
      path.startsWith('/sign-mandate')
    );
  }

  /** Post-signup wizard — no properties/contracts/tenants needed to paint. */
  static isOnboardingRoute(pathname: string): boolean {
    const path = pathname.split('?')[0] || pathname;
    return (
      path === '/onboarding' ||
      path.startsWith('/onboarding/') ||
      path === '/agency/onboarding' ||
      path.startsWith('/agency/onboarding/') ||
      path === '/provider/onboarding' ||
      path.startsWith('/provider/onboarding/')
    );
  }

  // Loading phases with weights for progress calculation
  private readonly loadingPhases: LoadingPhase[] = [
    {
      name: 'initializing',
      weight: 15,
      services: ['auth'],
      message: 'shared.loadingScreen.initializing',
      subMessage: 'shared.loadingScreen.initializingSub'
    },
    {
      name: 'loading-core',
      weight: 25,
      services: ['properties', 'contracts', 'tenants'],
      message: 'shared.loadingScreen.loadingCore',
      subMessage: 'shared.loadingScreen.loadingCoreSub'
    },
    {
      name: 'loading-data',
      weight: 40,
      services: ['expenses', 'globalIndex', 'financialCalculations'],
      message: 'shared.loadingScreen.enriching',
      subMessage: 'shared.loadingScreen.enrichingSub'
    },
    {
      name: 'finalizing',
      weight: 20,
      services: [],
      message: 'shared.loadingScreen.finalizing',
      subMessage: 'shared.loadingScreen.finalizingSub'
    }
  ];

  // Performance tracking
  private loadingStartTime: number = 0;
  private phaseStartTimes = new Map<string, number>();
  
  // Services
  private workspaceStore = inject(WorkspaceStore);
  private workspaceId$ = toObservable(this.workspaceStore.currentWorkspaceId);
  // #441 lot 3: which role is loading — decides whether the curtain needs to
  // wait on properties/contracts/tenants at all (see coreStoresApplicable).
  private workspaceType$ = toObservable(this.workspaceStore.currentWorkspace).pipe(
    map(ws => ws?.type ?? null)
  );

  // Public observables
  public loadingState$ = this.loadingStateSubject.asObservable();
  
  // Convenience observables
  public isLoading$ = this.loadingState$.pipe(
    map(state => state.isLoading),
    distinctUntilChanged()
  );

  public shouldShowLoadingScreen$ = this.loadingState$.pipe(
    map(state => state.isLoading && state.phase !== 'complete'),
    distinctUntilChanged()
  );

  /**
   * True when the Angular overlay should claim the HTML splash hand-off
   * (revealed chrome, or signup-init which reveals immediately).
   */
  public overlayClaimingSplash$ = this.loadingState$.pipe(
    map(state => state.isLoading && state.phase !== 'complete' && state.revealOverlay),
    distinctUntilChanged()
  );

  private authStore = inject(AuthStore);
  private isAuthenticated$ = toObservable(this.authStore.isAuthenticated);
  private authResolved$ = toObservable(this.authStore.authResolved);

  constructor(
    private unifiedLoadingService: UnifiedLoadingService,
    private appCoordination: AppCoordinationService,
    private propertyListStateService: PropertyListStateService,
    private router: Router
  ) {
    this.initializeCoordination();
  }

  ngOnDestroy(): void {
    this.clearRevealTimer();
    this.clearTourEscalation();
    this.stopSignupCreep();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the coordination system
   */
  private initializeCoordination(): void {
    console.log('🚀 AuthenticatedLoadingCoordinator: Initializing coordination system');

    // Start with initial loading state immediately
    this.startInitialLoading();

    // Gate on authResolved so the initial AuthStore `false` does not tear
    // the curtain down before Firebase restores the session.
    let isFirstResolution = true;
    combineLatest([this.authResolved$, this.isAuthenticated$]).pipe(
      takeUntil(this.destroy$),
      filter(([resolved]) => resolved),
      map(([, isAuthenticated]) => isAuthenticated),
      distinctUntilChanged()
    ).subscribe(isAuthenticated => {
      const first = isFirstResolution;
      isFirstResolution = false;
      this.handleResolvedAuthState(isAuthenticated, first);
    });
  }

  /**
   * What the auth-state subscription does on each RESOLVED emission.
   *
   * Split out of the subscription so it can be exercised directly: the boot
   * decision reads `bootedOnAuthRoute`, which is derived from
   * `window.location` at construction and cannot be steered from a spec.
   *
   * `isFirstResolution` separates the two ways a user becomes authenticated:
   *  - a session RESTORED at boot emits `true` straight away — it is the very
   *    first resolved value;
   *  - a login made during the session emits `false` (form on screen) and
   *    only later `true`.
   *
   * That distinction is the whole fix for #271. A restored session on a public
   * landing (`/tenant/invitation`, `/provider/claim`, `/book`…) has nothing to
   * load: the page is self-contained and the user is expected to stay on it.
   * Raising the curtain there strands it, because completion detection only
   * ever fires once the URL is NO LONGER an auth route — so the tenant was
   * left staring at a progress bar frozen at 67% with the invitation card
   * unreachable underneath. A cold login from `/auth/login` still gets its
   * curtain: it is a later emission, and it does navigate away.
   */
  private handleResolvedAuthState(isAuthenticated: boolean, isFirstResolution: boolean): void {
    if (!isAuthenticated) {
      console.log('🔒 AuthenticatedLoadingCoordinator: User not authenticated, showing authentication prompt');
      this.showAuthenticationRequired();
      return;
    }

    if (isFirstResolution && this.bootedOnAuthRoute && !this.workspaceSwitch) {
      console.log('🚪 AuthenticatedLoadingCoordinator: Session restored on a public landing, no curtain');
      this.stopLoading();
      return;
    }

    this.startAuthenticatedLoading();
  }

  /**
   * If the previous tab persisted a "switching workspace" marker before
   * triggering the page reload, read it here so the loading screen can
   * show "Switching to <name>" instead of the default "Welcome back".
   * Consumed once and cleared — the marker only affects this boot.
   */
  private readWorkspaceSwitchMarker(): WorkspaceSwitchMarker | null {
    try {
      const raw = sessionStorage.getItem('proporio_workspace_switch');
      if (!raw) return null;
      sessionStorage.removeItem('proporio_workspace_switch');
      const parsed = JSON.parse(raw);
      if (typeof parsed?.toName !== 'string') return null;

      const isRailEntry = (entry: unknown): entry is WorkspaceSwitchRailEntry => {
        if (!entry || typeof entry !== 'object') return false;
        const e = entry as Record<string, unknown>;
        return (
          typeof e['id'] === 'string' &&
          typeof e['name'] === 'string' &&
          typeof e['initials'] === 'string' &&
          typeof e['color'] === 'string'
        );
      };

      // A legacy {fromName,toName} marker (pre-rail) has no `toId`/`rail` —
      // normalized here to an empty rail, which the curtain already renders
      // as "destination only", same as a too-short rail. No separate branch.
      return {
        toId: typeof parsed.toId === 'string' ? parsed.toId : '',
        toName: parsed.toName,
        fromName: typeof parsed.fromName === 'string' ? parsed.fromName : '',
        rail: Array.isArray(parsed.rail) ? parsed.rail.filter(isRailEntry) : []
      };
    } catch {
      // Bad JSON or sessionStorage unavailable — fall through to default.
    }
    return null;
  }

  private workspaceSwitch: WorkspaceSwitchMarker | null = null;

  /**
   * When `true`, the regular phase-driven message updates are suspended
   * and `simple-register` drives the overlay directly via
   * {@link updateSignupInitProgress}. We resume the standard flow only
   * once {@link exitSignupInitMode} is called — typically right before
   * navigating the new user into the onboarding wizard.
   */
  private signupInitMode = false;

  /** Phase labels used while signup-init mode is active. */
  private static readonly SIGNUP_PHASE_LABELS: [string, string, string, string] = [
    'shared.loadingScreen.phase.account',
    'shared.loadingScreen.phase.workspace',
    'shared.loadingScreen.phase.permissions',
    'shared.loadingScreen.ready'
  ];

  /**
   * Start initial loading before authentication is determined
   */
  private startInitialLoading(): void {
    // Read the marker once at the very start of the boot. If we're mid-switch,
    // both the pre-auth and post-auth screens use a workspace-aware message.
    this.workspaceSwitch = this.readWorkspaceSwitchMarker();

    // After a logout we land on /auth/login via a hard reload. There's
    // nothing to "load" there — keep the overlay hidden so the user sees
    // the login form immediately. We do still respect a workspace-switch
    // marker (which only ever fires from the main shell, not auth routes).
    if (this.bootedOnAuthRoute && !this.workspaceSwitch) {
      console.log('🚀 AuthenticatedLoadingCoordinator: Booted on auth route, skipping splash');
      return;
    }

    console.log('🚀 AuthenticatedLoadingCoordinator: Starting initial loading phase');

    // `message`/`subMessage` are i18n keys, `message` interpolated with
    // `messageParams` by AuthenticatedLoadingScreenComponent — never
    // `translate.instant()` here, this service is constructed while the i18n
    // bundles are still loading.
    this.updateLoadingState({
      isLoading: true,
      phase: 'initializing',
      progress: 10,
      message: this.workspaceSwitch
        ? 'shared.loadingScreen.switchingTo'
        : 'shared.loadingScreen.starting',
      messageParams: this.workspaceSwitch
        ? { name: this.workspaceSwitch.toName }
        : undefined,
      subMessage: this.workspaceSwitch
        ? 'shared.loadingScreen.switchingWorkspaces'
        : 'shared.loadingScreen.startingSub',
      completedServices: [],
      totalServices: 6,
      showDetailedProgress: true,
      uiMode: this.workspaceSwitch ? 'workspace-switch' : 'minimal',
      workspaceSwitch: this.workspaceSwitch ?? undefined,
      revealOverlay: false
    });
  }

  /**
   * Show authentication required state
   */
  private showAuthenticationRequired(): void {
    console.log('🔒 AuthenticatedLoadingCoordinator: Showing authentication required');
    this.clearRevealTimer();

    this.updateLoadingState({
      isLoading: false,
      phase: 'complete',
      progress: 100,
      message: 'shared.loadingScreen.authRequired',
      subMessage: 'shared.loadingScreen.authRequiredSub',
      completedServices: [],
      totalServices: 6,
      showDetailedProgress: false,
      revealOverlay: false
    });
  }

  /**
   * Start the authenticated loading process
   */
  private startAuthenticatedLoading(): void {
    this.loadingStartTime = Date.now();
    this.phaseStartTimes.clear();

    // Signup-init mode: simple-register drives the overlay directly until
    // it calls `exitSignupInitMode()`. We defer everything (state update +
    // coordination subscription + completion detection) so we don't fight
    // the signup messages or tear the overlay down too early.
    if (this.signupInitMode) {
      console.log('🆕 AuthenticatedLoadingCoordinator: Deferring authenticated loading until signup-init mode exits');
      return;
    }

    // Past the signup-init guard: the creep belongs to that mode alone, and a
    // stray timer here would keep nudging the standard flow's bar.
    this.stopSignupCreep();

    // Open minimal (or the pinned workspace-switch curtain); escalate to tour
    // if the wait exceeds 2 s.
    this.updateLoadingState({
      isLoading: true,
      phase: 'initializing',
      progress: 25,
      message: this.workspaceSwitch
        ? 'shared.loadingScreen.switchingTo'
        : 'shared.loadingScreen.welcomeBack',
      messageParams: this.workspaceSwitch
        ? { name: this.workspaceSwitch.toName }
        : undefined,
      subMessage: this.workspaceSwitch
        ? 'shared.loadingScreen.switchingWorkspaces'
        : 'shared.loadingScreen.loadingDashboard',
      completedServices: ['auth'],
      totalServices: 6,
      showDetailedProgress: false,
      phaseLabels: undefined,
      loadingTip: undefined,
      uiMode: this.workspaceSwitch ? 'workspace-switch' : 'minimal',
      workspaceSwitch: this.workspaceSwitch ?? undefined,
      // Build only when nothing has assembled the mark yet. At boot the HTML
      // splash already did, and on the way out of signup-init this very curtain
      // did — in both cases the picture must be taken over, not restarted.
      markPhase: this.markAssembled ? 'continue' : 'restart',
      revealOverlay: this.userInitiated
    });
    this.markAssembled = true;
    this.scheduleTourEscalation();

    if (!this.userInitiated) {
      this.scheduleOverlayReveal();
    }

    // Coordinate all loading services (one-shot per boot cycle)
    this.coordinateLoadingServices();
  }

  /**
   * After {@link TOUR_ESCALATE_MS} of continuous loading, promote the curtain
   * to the guided tour. Short boots stay on the quiet minimal chrome; a
   * workspace-switch curtain keeps its rail whatever the wait.
   */
  private scheduleTourEscalation(): void {
    this.clearTourEscalation();
    this.tourEscalateTimer = setTimeout(() => {
      this.tourEscalateTimer = null;
      const current = this.loadingStateSubject.value;
      if (current.isLoading && current.uiMode === 'minimal') {
        this.updateLoadingState({ uiMode: 'tour', showDetailedProgress: true });
      }
    }, AuthenticatedLoadingCoordinatorService.TOUR_ESCALATE_MS);
  }

  private clearTourEscalation(): void {
    if (this.tourEscalateTimer !== null) {
      clearTimeout(this.tourEscalateTimer);
      this.tourEscalateTimer = null;
    }
  }

  private markTourSeen(): void {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AuthenticatedLoadingCoordinatorService.TOUR_SEEN_KEY, '1');
      }
    } catch {
      // ignore
    }
  }

  /**
   * Raise the curtain NOW, before the sign-in call that will eventually flip
   * the auth state. Call it from the click handler, not after the promise
   * resolves.
   *
   * Without it the login screen has a hole: `authService.login()` resolves, the
   * button's spinner stops, and for ~250 ms (OVERLAY_REVEAL_MS) the form stands
   * there intact with no indication that anything is happening — then the
   * curtain drops on top. This is the same trick `enterSignupInitMode()` has
   * always used; it is simply not specific to signup.
   */
  public beginUserInitiatedTransition(message = 'shared.loadingScreen.signingIn'): void {
    this.userInitiated = true;
    this.markAssembled = true; // this state IS the build
    this.loadingStartTime = Date.now();
    this.clearRevealTimer();

    this.updateLoadingState({
      isLoading: true,
      phase: 'initializing',
      progress: 10,
      message,
      subMessage: 'shared.loadingScreen.loadingDashboard',
      completedServices: [],
      totalServices: 6,
      showDetailedProgress: false,
      phaseLabels: undefined,
      loadingTip: undefined,
      uiMode: 'minimal',
      markPhase: 'restart',
      revealOverlay: true
    });
    this.scheduleTourEscalation();
  }

  /**
   * Undo {@link beginUserInitiatedTransition} when the sign-in FAILED, so the
   * user gets their form back with the error rather than a curtain that waits
   * for a navigation which will never come.
   */
  public abortUserInitiatedTransition(): void {
    if (!this.userInitiated) return;
    this.userInitiated = false;
    this.markAssembled = false;
    this.clearRevealTimer();
    this.clearTourEscalation();

    this.updateLoadingState({
      isLoading: false,
      phase: 'complete',
      progress: 100,
      message: 'shared.loadingScreen.ready',
      subMessage: undefined,
      showDetailedProgress: false,
      uiMode: undefined,
      revealOverlay: false
    });
  }

  private scheduleOverlayReveal(): void {
    this.clearRevealTimer();
    this.revealTimer = setTimeout(() => {
      this.revealTimer = null;
      if (this.loadingStateSubject.value.isLoading) {
        this.updateLoadingState({ revealOverlay: true });
      }
    }, AuthenticatedLoadingCoordinatorService.OVERLAY_REVEAL_MS);
  }

  private clearRevealTimer(): void {
    if (this.revealTimer !== null) {
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
    }
  }

  // ===========================================================================
  // Signup-init mode (fresh signup → "Preparing your workspace" overlay)
  // ===========================================================================

  /**
   * Switch the overlay into signup-init mode. Call this BEFORE
   * `authService.signup()` so the auth-state flip doesn't briefly show
   * "Welcome back!" before the first backend status arrives.
   */
  public enterSignupInitMode(): void {
    this.signupInitMode = true;
    this.markAssembled = true; // this state IS the build
    this.workspaceSwitch = null; // signup takes priority over a stale switch marker
    this.loadingStartTime = Date.now();
    this.phaseStartTimes.clear();

    this.updateLoadingState({
      isLoading: true,
      phase: 'initializing',
      progress: 5,
      message: 'shared.loadingScreen.creatingAccount',
      subMessage: 'shared.loadingScreen.creatingAccountSub',
      completedServices: [],
      totalServices: 4,
      showDetailedProgress: true,
      phaseLabels: AuthenticatedLoadingCoordinatorService.SIGNUP_PHASE_LABELS,
      loadingTip: 'shared.loadingScreen.tip.default',
      // Same adaptive surface as login/boot: minimal first, tour after 2 s.
      uiMode: 'minimal',
      // Opens over the register form, with no HTML splash in front of it: the
      // mark assembles here.
      markPhase: 'restart',
      revealOverlay: true
    });
    this.scheduleTourEscalation();

    // Start drifting immediately: the `createUserWithEmailAndPassword`
    // round-trip happens before the first milestone, and a bar parked on 5 %
    // for it reads as a hang.
    this.startSignupCreep(30);
  }

  /**
   * Push a fresh status update from the backend `users/{uid}/signup_status/initialization`
   * document into the overlay state. Mapped onto the existing phase enum so
   * the step indicators stay coherent.
   */
  public updateSignupInitProgress(status: SignupInitProgress): void {
    if (!this.signupInitMode) return;

    const phase = this.mapSignupStepToPhase(status.step);
    const message = this.signupInitTitle(status.step);
    const subMessage = status.message ?? this.signupInitDefaultSubMessage(status.step);
    const progress = typeof status.progress === 'number'
      ? Math.min(95, Math.max(this.loadingStateSubject.value.progress, status.progress))
      : this.loadingStateSubject.value.progress;

    this.updateLoadingState({
      isLoading: true,
      phase,
      progress,
      message,
      subMessage,
      showDetailedProgress: true,
      totalServices: 4,
      phaseLabels: AuthenticatedLoadingCoordinatorService.SIGNUP_PHASE_LABELS,
      loadingTip: this.signupInitTip(status.step),
      // uiMode left to the escalator — do not snap back to minimal.
      revealOverlay: true
    });
    // markPhase is deliberately absent: the mark was assembled when this mode
    // opened and must not restart on every backend status push.
  }

  /**
   * Exit signup-init mode and resume the standard authenticated-loading flow.
   * Should be called after `waitForPostSignupInitialization` resolves and the
   * register component is about to navigate to the onboarding/dashboard route.
   */
  public exitSignupInitMode(): void {
    if (!this.signupInitMode) return;
    this.signupInitMode = false;
    this.stopSignupCreep();

    // Bridge: keep the overlay visible (still loading) but switch into the
    // regular flow so completion detection can tear it down once the next
    // route stabilises.
    this.updateLoadingState({
      isLoading: true,
      phase: 'finalizing',
      progress: Math.max(this.loadingStateSubject.value.progress, 95),
      message: 'shared.loadingScreen.almostReady',
      subMessage: 'shared.loadingScreen.loadingDashboard',
      showDetailedProgress: true,
      totalServices: 6,
      phaseLabels: undefined,
      loadingTip: undefined,
      uiMode: 'minimal',
      // The signup curtain already built the mark; carry it over whole.
      markPhase: 'continue',
      revealOverlay: true
    });

    // Allow a fresh coordination cycle after signup-init deferred the first one.
    // `userInitiated` keeps the curtain up without the anti-flash delay — there
    // is no HTML splash behind it, only the register form.
    this.coordinating = false;
    this.userInitiated = true;
    this.startAuthenticatedLoading();
  }

  /**
   * Abort signup-init mode after a FAILED signup (e.g. the email is already
   * registered, so `createUserWithEmailAndPassword` threw and no account was
   * created). Unlike {@link exitSignupInitMode}, this must NOT bridge into the
   * authenticated-loading flow: the user is still unauthenticated and sitting
   * on `/auth/register`, so completion detection — which only tears the overlay
   * down on a NavigationEnd landing on an app route — would never fire and the
   * user would stay stuck on the "Creating your account" splash forever. We
   * fully stop the overlay so the register form (and its error) becomes visible.
   */
  public abortSignupInitMode(): void {
    if (!this.signupInitMode) return;
    this.signupInitMode = false;
    this.stopSignupCreep();
    this.stopLoading();
  }

  // ---------------------------------------------------------------------------
  // Client-side milestones + creep
  // ---------------------------------------------------------------------------

  /**
   * Progress, ceiling, phase and copy for each client-observable checkpoint.
   *
   * The ceilings are what the creep is allowed to drift up to while waiting for
   * the next real event, and they are deliberately just under the backend step
   * that follows (30 < 33, 64 < 66): the bar keeps moving, but it can never
   * claim a milestone that hasn't happened.
   */
  private static readonly SIGNUP_CLIENT_MILESTONES: Record<
    SignupClientMilestone,
    {
      progress: number;
      ceiling: number;
      phase: AuthenticatedLoadingState['phase'];
      message: string;
      subMessage: string;
      tip: string;
    }
  > = {
    account_created: {
      progress: 20,
      ceiling: 30,
      phase: 'initializing',
      message: 'shared.loadingScreen.signup.accountCreated',
      subMessage: 'shared.loadingScreen.creatingAccountSub',
      tip: 'shared.loadingScreen.tip.default',
    },
    user_doc_written: {
      progress: 33,
      ceiling: 45,
      phase: 'loading-core',
      message: 'shared.loadingScreen.signup.preparingWorkspace',
      subMessage: 'shared.loadingScreen.signup.buildingWorkspace',
      tip: 'shared.loadingScreen.tip.userCreated',
    },
    awaiting_backend: {
      progress: 40,
      ceiling: 64,
      phase: 'loading-core',
      message: 'shared.loadingScreen.signup.preparingWorkspace',
      subMessage: 'shared.loadingScreen.signup.buildingWorkspace',
      tip: 'shared.loadingScreen.tip.userCreated',
    },
    session_ready: {
      progress: 96,
      ceiling: 98,
      phase: 'finalizing',
      message: 'shared.loadingScreen.signup.almostThere',
      subMessage: 'shared.loadingScreen.loadingDashboard',
      tip: 'shared.loadingScreen.tip.claimsReady',
    },
  };

  /** How often the bar drifts toward the current ceiling, in ms. */
  private static readonly SIGNUP_CREEP_INTERVAL_MS = 400;

  private signupCreepTimer: ReturnType<typeof setInterval> | null = null;
  private signupCreepCeiling = 0;

  /**
   * Record a client-observable checkpoint. No-op outside signup-init mode, so
   * callers can wire it unconditionally.
   */
  public markSignupMilestone(step: SignupClientMilestone): void {
    if (!this.signupInitMode) return;
    const milestone =
      AuthenticatedLoadingCoordinatorService.SIGNUP_CLIENT_MILESTONES[step];
    if (!milestone) return;

    this.updateLoadingState({
      isLoading: true,
      phase: milestone.phase,
      // Same monotonic clamp as the backend path: a milestone may only raise.
      progress: Math.min(
        98,
        Math.max(this.loadingStateSubject.value.progress, milestone.progress),
      ),
      message: milestone.message,
      subMessage: milestone.subMessage,
      showDetailedProgress: true,
      totalServices: 4,
      phaseLabels: AuthenticatedLoadingCoordinatorService.SIGNUP_PHASE_LABELS,
      loadingTip: milestone.tip,
      revealOverlay: true,
      // markPhase stays absent: the mark was assembled on entry and must not
      // restart on every checkpoint.
      // uiMode left to the escalator.
    });

    this.startSignupCreep(milestone.ceiling);
  }

  /**
   * Drift the bar toward `ceiling` so a slow backend step never looks frozen.
   *
   * Ease-out: each tick covers a fraction of the remaining gap, so the motion
   * is fast at first and settles at the ceiling instead of hitting it. Strictly
   * increasing by construction — this must never violate the monotonic
   * guarantee the overlay relies on.
   */
  private startSignupCreep(ceiling: number): void {
    this.stopSignupCreep();
    this.signupCreepCeiling = ceiling;
    this.signupCreepTimer = setInterval(
      () => this.tickSignupCreep(),
      AuthenticatedLoadingCoordinatorService.SIGNUP_CREEP_INTERVAL_MS,
    );
  }

  private tickSignupCreep(): void {
    if (!this.signupInitMode) {
      this.stopSignupCreep();
      return;
    }
    const current = this.loadingStateSubject.value.progress;
    if (current >= this.signupCreepCeiling) {
      this.stopSignupCreep();
      return;
    }
    const step = Math.max(0.25, (this.signupCreepCeiling - current) * 0.06);
    // Only `progress` is passed: updateLoadingState spreads over the current
    // state, so message/phase/tip survive untouched and the phase-transition
    // log never fires from a creep tick.
    this.updateLoadingState({
      progress: Math.min(this.signupCreepCeiling, current + step),
    });
  }

  private stopSignupCreep(): void {
    if (this.signupCreepTimer !== null) {
      clearInterval(this.signupCreepTimer);
      this.signupCreepTimer = null;
    }
  }

  private mapSignupStepToPhase(step?: string): AuthenticatedLoadingState['phase'] {
    switch (step) {
      case 'user_created': return 'loading-core';
      case 'workspace_created': return 'loading-data';
      case 'claims_ready': return 'finalizing';
      default: return 'initializing';
    }
  }

  private signupInitTitle(step?: string): string {
    switch (step) {
      case 'user_created': return 'shared.loadingScreen.signup.preparingWorkspace';
      case 'workspace_created': return 'shared.loadingScreen.signup.securing';
      case 'claims_ready': return 'shared.loadingScreen.signup.almostThere';
      default: return 'shared.loadingScreen.creatingAccount';
    }
  }

  private signupInitDefaultSubMessage(step?: string): string {
    switch (step) {
      case 'user_created': return 'shared.loadingScreen.signup.buildingWorkspace';
      case 'workspace_created': return 'shared.loadingScreen.signup.configuringPermissions';
      case 'claims_ready': return 'shared.loadingScreen.loadingDashboard';
      default: return 'shared.loadingScreen.creatingAccountSub';
    }
  }

  private signupInitTip(step?: string): string {
    switch (step) {
      case 'user_created':
        return 'shared.loadingScreen.tip.userCreated';
      case 'workspace_created':
        return 'shared.loadingScreen.tip.workspaceCreated';
      case 'claims_ready':
        return 'shared.loadingScreen.tip.claimsReady';
      default:
        return 'shared.loadingScreen.tip.default';
    }
  }

  /**
   * Coordinate all loading services and track their progress
   */
  private coordinateLoadingServices(): void {
    if (this.coordinating) {
      return;
    }
    this.coordinating = true;

    // Combine all relevant loading states
    combineLatest([
      this.unifiedLoadingService.progress$,
      this.appCoordination.appLoadingState$,
      this.propertyListStateService.isLoading$,
      this.appCoordination.serviceLoadingState$
    ]).pipe(
      // Debounce to prevent excessive updates
      debounceTime(100),
      // Only process when we're in loading mode
      filter(() => this.loadingStateSubject.value.isLoading),
      tap(([unifiedProgress, appState, propertyLoading, serviceState]) => {
        console.log('📊 AuthenticatedLoadingCoordinator: Progress update', {
          unified: unifiedProgress,
          app: appState,
          properties: propertyLoading,
          services: serviceState
        });
      }),
      takeUntil(this.destroy$)
    ).subscribe(([unifiedProgress, appState, propertyLoading, serviceState]) => {
      // While signup-init mode is active, the register component owns the
      // overlay state — skip phase-driven recomputations that would clobber
      // the "Preparing your workspace…" messages.
      if (this.signupInitMode) return;
      this.calculateAndUpdateProgress(unifiedProgress, appState, propertyLoading, serviceState);
    });

    // Set up completion detection
    this.setupCompletionDetection();
  }

  /**
   * Calculate overall progress and update loading state
   */
  private calculateAndUpdateProgress(
    unifiedProgress: UnifiedProgressShape,
    appState: AppCoordinationStateShape,
    propertyLoading: boolean,
    serviceState: ServiceLoadingStateShape
  ): void {
    const completedServices = this.getCompletedServices(unifiedProgress, appState, serviceState);
    const currentPhase = this.determineCurrentPhase(completedServices, propertyLoading);
    const progress = this.calculateProgress(currentPhase, completedServices);
    const timeRemaining = this.estimateTimeRemaining(progress);

    const phaseConfig = this.loadingPhases.find(p => p.name === currentPhase);

    // Keep the workspace-switch context visible across every phase update —
    // otherwise the dynamic phase messages would overwrite it back to
    // "Loading core data..." etc.
    const message = this.workspaceSwitch
      ? 'shared.loadingScreen.switchingTo'
      : (phaseConfig?.message || 'Loading...');
    const messageParams = this.workspaceSwitch
      ? { name: this.workspaceSwitch.toName }
      : undefined;
    const subMessage = this.workspaceSwitch
      ? 'shared.loadingScreen.switchingWorkspaces'
      : phaseConfig?.subMessage;

    // The progress is recomputed from scratch on every emission, and the three
    // sources feeding getCompletedServices() can momentarily report fewer
    // services than before (an empty list puts us back on 'initializing' at 0%).
    // Clamping to the previous value stops the bar from visibly rolling back
    // mid-load — same monotonic guarantee updateSignupInitProgress() already
    // gives the signup flow. Explicit resets (a new load, a workspace switch)
    // go through updateLoadingState() directly, so they still move it down.
    const monotonicProgress = Math.max(progress, this.loadingStateSubject.value.progress);

    this.updateLoadingState({
      isLoading: true,
      phase: currentPhase,
      progress: Math.min(monotonicProgress, 95), // Never show 100% until actually complete
      message,
      messageParams,
      subMessage,
      completedServices,
      totalServices: 6,
      showDetailedProgress: this.loadingStateSubject.value.uiMode !== 'minimal',
      estimatedTimeRemaining: timeRemaining
    });
  }

  /**
   * Determine which services have completed loading
   */
  private getCompletedServices(unifiedProgress: UnifiedProgressShape, appState: AppCoordinationStateShape, serviceState: ServiceLoadingStateShape): string[] {
    const completed: string[] = [];

    // Check unified loading service progress
    if (unifiedProgress?.completedServices) {
      completed.push(...unifiedProgress.completedServices);
    }

    // Check app coordination state
    if (appState?.servicesLoaded) {
      completed.push('core-services');
    }

    // Check individual service states
    if (serviceState) {
      Object.entries(serviceState).forEach(([service, isLoaded]) => {
        if (isLoaded && !completed.includes(service)) {
          completed.push(service);
        }
      });
    }

    return [...new Set(completed)]; // Remove duplicates
  }

  /**
   * Determine current loading phase based on completed services
   */
  private determineCurrentPhase(completedServices: string[], propertyLoading: boolean): AuthenticatedLoadingState['phase'] {
    const hasAuth = completedServices.includes('auth') || completedServices.length > 0;
    const hasCoreServices = completedServices.includes('properties') && completedServices.includes('contracts');
    const hasDataServices = completedServices.includes('expenses') && completedServices.includes('globalIndex');
    const hasFinancials = completedServices.includes('financialCalculations') || !propertyLoading;

    if (!hasAuth) return 'initializing';
    if (!hasCoreServices) return 'loading-core';
    if (!hasDataServices || !hasFinancials) return 'loading-data';

    // Never 'complete' here (#510 pt.49): that phase is completeLoading()'s
    // alone to declare, gated on router-settled + coreStoresHydrated in
    // setupCompletionDetection. Returning it from this periodic recompute made
    // phase flip to 'complete' — unhiding the page via shouldShowLoadingScreen$
    // — while isLoading stayed true, leaving the overlay (gated on isLoading
    // alone) stuck on top of an already-rendered cockpit.
    return 'finalizing';
  }

  /**
   * Calculate overall progress percentage
   */
  private calculateProgress(phase: AuthenticatedLoadingState['phase'], completedServices: string[]): number {
    const phaseIndex = this.loadingPhases.findIndex(p => p.name === phase);
    if (phaseIndex === -1) return 0;

    // Calculate progress based on completed phases
    let totalProgress = 0;
    
    // Add weight of completed phases
    for (let i = 0; i < phaseIndex; i++) {
      totalProgress += this.loadingPhases[i].weight;
    }

    // Add partial progress of current phase
    const currentPhase = this.loadingPhases[phaseIndex];
    const phaseProgress = this.calculatePhaseProgress(currentPhase, completedServices);
    totalProgress += (currentPhase.weight * phaseProgress) / 100;

    return Math.min(totalProgress, 95); // Cap at 95% until complete
  }

  /**
   * Calculate progress within a specific phase
   */
  private calculatePhaseProgress(phase: LoadingPhase, completedServices: string[]): number {
    if (phase.services.length === 0) return 50; // For phases without specific services

    const completedInPhase = phase.services.filter(service => 
      completedServices.includes(service)
    ).length;

    return (completedInPhase / phase.services.length) * 100;
  }

  /**
   * Estimate remaining time based on current progress and elapsed time
   */
  private estimateTimeRemaining(progress: number): number | undefined {
    if (progress <= 5) return undefined; // Too early to estimate

    const elapsedTime = Date.now() - this.loadingStartTime;
    const estimatedTotalTime = (elapsedTime / progress) * 100;
    const remainingTime = estimatedTotalTime - elapsedTime;

    // Return remaining time in seconds, capped at reasonable values
    return Math.max(0, Math.min(remainingTime / 1000, 30));
  }

  /**
   * Check if the current URL is a detail page
   */
  private isDetailPage(url: string): boolean {
    // Property detail pages: /properties/:id/*
    if (url.match(/\/properties\/[^/]+\/\w+/)) return true;
    
    // Tenant detail pages: /tenants/:id/*
    if (url.match(/\/tenants\/[^/]+\/\w+/)) return true;
    
    // Contract detail pages: /contracts/detail/:id/*
    if (url.match(/\/contracts\/detail\/[^/]+/)) return true;
    
    // Building detail pages: /building/:id/*
    if (url.match(/\/building\/[^/]+/)) return true;
    
    // Loan detail pages: /loans/detail/:id/*
    if (url.match(/\/loans\/detail\/[^/]+/)) return true;
    
    // Service provider detail pages: /service-providers/:id
    if (url.match(/\/service-providers\/[^/]+$/)) return true;
    
    // Repair detail pages: /properties/:id/repairs/detail/:id
    if (url.match(/\/properties\/[^/]+\/repairs\/detail\/[^/]+/)) return true;
    
    // Maintenance detail pages: /properties/:id/maintenance/detail/:id
    if (url.match(/\/properties\/[^/]+\/maintenance\/detail\/[^/]+/)) return true;
    
    return false;
  }

  /**
   * Check if the current URL is a list page that needs property data
   */
  private isListPage(url: string): boolean {
    // Property list page
    if (url.includes('/properties/list') || url === '/properties') return true;
    
    // Dashboard pages that need property data
    if (url.includes('/dashboard') || url === '/') return true;
    
    // Tenant list page
    if (url.includes('/tenants/list') || url === '/tenants') return true;
    
    // Contract list page
    if (url.includes('/contracts/list') || url === '/contracts') return true;
    
    // Loan list page
    if (url.includes('/loans/list') || url === '/loans') return true;
    
    return false;
  }

  /**
   * Check if the current URL is an edit/add page
   */
  private isEditPage(url: string): boolean {
    // Edit pages: /tenants/edit/:id, /contracts/edit/:id, /loans/edit/:id
    if (url.match(/\/(tenants|contracts|loans)\/edit\/[^/]+/)) return true;
    
    // Add pages: /properties/add, /tenants/add, /contracts/add, /loans/add
    if (url.match(/\/(properties|tenants|contracts|loans)\/add/)) return true;
    
    return false;
  }

  /**
   * Set up detection for when loading is complete.
   *
   * Route type and timeouts are derived from the latest settled NavigationEnd
   * so a cold login that starts on /auth/login still picks the cockpit/list
   * timeout after navigation — not the URL captured at subscribe time.
   */
  private setupCompletionDetection(): void {
    const currentUrl$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects || e.url),
      startWith(this.router.url)
    );

    const routerSettled$ = currentUrl$.pipe(
      map(url => !AuthenticatedLoadingCoordinatorService.isAuthRoute(url)),
      distinctUntilChanged(),
      // After NavigationEnd, child views may need a frame to stabilize.
      switchMap(settled => settled ? timer(150).pipe(map(() => true)) : of(false))
    );

    const coreStoresHydrated$ = this.appCoordination.serviceLoadingState$.pipe(
      map(state => !!(state?.properties && state?.contracts && state?.tenants)),
      distinctUntilChanged(),
      startWith(false)
    );

    const timeoutReached$ = currentUrl$.pipe(
      map(url => this.timeoutMsForUrl(url)),
      distinctUntilChanged(),
      switchMap(ms => timer(ms).pipe(map(() => true), startWith(false)))
    );

    const isComplete = (
      dataReady: boolean,
      routerSettled: boolean,
      timeoutReached: boolean
    ) => routerSettled && (dataReady || timeoutReached);

    // #441 lot 2: `readyToDisplay` (AppCoordinationService) dropped from the
    // gate entirely — it waits on financial enrichment across the whole
    // portfolio, the global index and property groups, none of which the
    // FIRST screen (the Cockpit) shows. Page skeletons read that data once it
    // actually arrives; the curtain no longer waits for it.
    //
    // #441 lot 3: whether the curtain waits on coreStoresHydrated at all now
    // depends on the workspace's role — a tenant/service-provider workspace's
    // own properties/contracts/tenants are permanently empty (see
    // `coreStoresApplicable`), so waiting on them there means the curtain can
    // only ever come down on its fail-safe timer. Unknown role stays
    // conservative: still required, same as before this lot.
    const coreDataRequired$ = this.workspaceType$.pipe(
      map(type => coreStoresApplicable(type)),
      distinctUntilChanged()
    );

    const dataComplete$ = combineLatest([
      coreDataRequired$,
      coreStoresHydrated$,
      this.workspaceId$.pipe(map(id => !!id), startWith(false)),
      routerSettled$,
      timeoutReached$,
      currentUrl$
    ]).pipe(
      filter(([coreDataRequired, coreStoresHydrated, workspaceReady, routerSettled, timeoutReached, url]) => {
        const onboardingRoute =
          AuthenticatedLoadingCoordinatorService.isOnboardingRoute(url);
        // Wizard screens do not need properties/contracts/tenants — waiting on
        // empty core stores only stretched the curtain for new signups.
        const dataReady = workspaceReady && (
          onboardingRoute || !coreDataRequired || coreStoresHydrated
        );
        const complete = isComplete(dataReady, routerSettled, timeoutReached);
        console.log('🔍 AuthenticatedLoadingCoordinator: Completion check', {
          coreDataRequired,
          coreStoresHydrated,
          workspaceReady,
          routerSettled,
          timeoutReached,
          onboardingRoute,
          url,
          isComplete: complete
        });
        return complete;
      })
    );

    // Absolute last resort. Everything above is ANDed with `routerSettled`, so
    // a curtain raised while the URL is (and stays) a public route could never
    // come down — that is what froze #271 on /tenant/invitation, and the same
    // trap catches an accept whose navigation to /tenant/dashboard is refused.
    // Well past the per-route fail-safes (1.5-2 s), so it never pre-empts the
    // normal path nor a cold login that has yet to navigate; but no wait is
    // allowed to be infinite (DESIGN.md §13).
    const absoluteFailsafe$ = timer(
      AuthenticatedLoadingCoordinatorService.ABSOLUTE_FAILSAFE_MS
    ).pipe(
      tap(() => console.warn('⏱️ AuthenticatedLoadingCoordinator: absolute fail-safe reached, tearing the curtain down'))
    );

    merge(dataComplete$, absoluteFailsafe$).pipe(
      takeUntil(this.destroy$),
      take(1)
    ).subscribe(() => this.completeLoading());
  }

  private timeoutMsForUrl(url: string): number {
    if (this.isDetailPage(url)) return 1500;
    if (this.isListPage(url)) return 2000;
    if (this.isEditPage(url)) return 1500;
    return 2000;
  }

  /**
   * Complete the loading process
   */
  private completeLoading(): void {
    const totalTime = Date.now() - this.loadingStartTime;
    console.log(`🏁 AuthenticatedLoadingCoordinator: Loading completed in ${totalTime}ms`);
    this.clearRevealTimer();
    this.clearTourEscalation();
    this.stopSignupCreep();
    this.markTourSeen();
    this.userInitiated = false;
    this.markAssembled = false;

    this.updateLoadingState({
      isLoading: false,
      phase: 'complete',
      progress: 100,
      message: 'shared.loadingScreen.appReady',
      subMessage: 'shared.loadingScreen.welcomeBack',
      completedServices: ['properties', 'contracts', 'loans', 'tenants', 'expenses', 'globalIndex', 'financialCalculations'],
      totalServices: 6,
      showDetailedProgress: false,
      revealOverlay: false
    });
  }

  /**
   * Stop loading (for unauthenticated users)
   */
  private stopLoading(): void {
    this.clearRevealTimer();
    this.clearTourEscalation();
    this.userInitiated = false;
    this.markAssembled = false;
    this.updateLoadingState({
      isLoading: false,
      phase: 'complete',
      progress: 100,
      message: 'shared.loadingScreen.ready',
      completedServices: [],
      totalServices: 6,
      showDetailedProgress: false,
      revealOverlay: false
    });
  }

  /**
   * Update the loading state
   */
  private updateLoadingState(state: Partial<AuthenticatedLoadingState>): void {
    const currentState = this.loadingStateSubject.value;
    const newState = { ...currentState, ...state };
    
    // Track phase transitions
    if (newState.phase !== currentState.phase) {
      console.log(`🔄 AuthenticatedLoadingCoordinator: Phase transition: ${currentState.phase} → ${newState.phase}`);
      this.phaseStartTimes.set(newState.phase, Date.now());
    }

    this.loadingStateSubject.next(newState);
  }

  /**
   * Force refresh of the loading state (for debugging)
   */
  public refreshLoadingState(): void {
    if (this.loadingStateSubject.value.isLoading) {
      console.log('🔄 AuthenticatedLoadingCoordinator: Force refreshing loading state');
      this.coordinateLoadingServices();
    }
  }

  /**
   * Get performance metrics for debugging
   */
  public getPerformanceMetrics(): PerformanceMetrics {
    return {
      totalLoadingTime: this.loadingStartTime ? Date.now() - this.loadingStartTime : 0,
      phaseTimings: Object.fromEntries(this.phaseStartTimes),
      currentState: this.loadingStateSubject.value
    };
  }
}

// Structural types describing the shapes read from the coordinated sub-services.
// Each source publishes its own richer interface — these are just the fields
// this coordinator consumes.
interface UnifiedProgressShape {
  completedServices?: string[];
  currentService?: string;
  progress?: number;
}

interface AppCoordinationStateShape {
  servicesLoaded?: boolean;
  authenticationComplete?: boolean;
  workspaceReady?: boolean;
  firebaseConnected?: boolean;
}

// Reuse the concrete `ServiceLoadingState` from `app-coordination.service`.
// Imported where needed at the top of the file; typed as Partial here because
// callers may emit before every field is populated.
import type { ServiceLoadingState as AppServiceLoadingState } from '@core/services/infra/app-coordination.service';
type ServiceLoadingStateShape = Partial<AppServiceLoadingState> | null | undefined;

interface PerformanceMetrics {
  totalLoadingTime: number;
  phaseTimings: Record<string, number>;
  currentState: unknown;
}
